import React, { useState } from "react";
import { RoutingRule, Provider, Merchant } from "@/shared/api/entities";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Badge } from "@/shared/components/ui/badge";
import { Switch } from "@/shared/components/ui/switch";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/shared/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import {
  Plus,
  Route,
  MoreHorizontal,
  Pencil,
  Trash2,
  GripVertical,
  CheckCircle2,
  XCircle,
  ArrowRight,
  Shuffle,
  GitBranch,
  Scale,
  AlertCircle,
} from "lucide-react";

const C = {
  bg: "#060C18",
  card: "#0E1C35",
  cardHigh: "#122040",
  cardHover: "#162B4D",
  border: "rgba(255,255,255,0.07)",
  borderHover: "rgba(34,211,238,0.35)",
  cyan: "#22D3EE",
  cyanSoft: "rgba(34,211,238,0.08)",
  blue: "#3B82F6",
  blueSoft: "rgba(59,130,246,0.08)",
  purple: "#A78BFA",
  purpleSoft: "rgba(167,139,250,0.08)",
  emerald: "#10B981",
  emeraldSoft: "rgba(16,185,129,0.08)",
  amber: "#F59E0B",
  amberSoft: "rgba(245,158,11,0.08)",
  rose: "#FB7185",
  t1: "#EFF6FF",
  t2: "#93B4D4",
  t3: "#4D6E90",
  t4: "#2A4060",
};

const DARK_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap');

.routing-dark, .routing-dark * {
  font-family: 'Outfit', system-ui, sans-serif !important;
  box-sizing: border-box;
}

.routing-dark input[type="text"],
.routing-dark input[type="number"],
.routing-dark input[type="date"],
.routing-dark input:not([type="checkbox"]):not([type="radio"]) {
  background: #0E1C35 !important;
  border: 1px solid rgba(255,255,255,0.09) !important;
  color: #EFF6FF !important;
  border-radius: 10px !important;
  height: 40px !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  transition: all .2s cubic-bezier(0.4,0,0.2,1) !important;
  padding: 0 14px !important;
}

.routing-dark input::placeholder {
  color: #4D6E90 !important;
}

.routing-dark input:hover {
  border-color: rgba(255,255,255,0.14) !important;
  background: #162B4D !important;
}

.routing-dark input:focus {
  outline: none !important;
  border-color: rgba(34,211,238,0.5) !important;
  box-shadow: 0 0 0 3px rgba(34,211,238,0.12) !important;
  background: #122040 !important;
}

::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

::-webkit-scrollbar-track {
  background: rgba(14,28,53,0.3);
  border-radius: 10px;
}

::-webkit-scrollbar-thumb {
  background: rgba(34,211,238,0.2);
  border-radius: 10px;
  transition: background .2s;
}

::-webkit-scrollbar-thumb:hover {
  background: rgba(34,211,238,0.35);
}
`;

const routingModeConfig = {
  priority: { icon: GitBranch, label: "Priority", color: C.blue, bg: C.blueSoft, border: "rgba(59,130,246,0.2)" },
  round_robin: { icon: Shuffle, label: "Round Robin", color: C.purple, bg: C.purpleSoft, border: "rgba(167,139,250,0.2)" },
  weighted: { icon: Scale, label: "Weighted", color: C.amber, bg: C.amberSoft, border: "rgba(245,158,11,0.2)" },
  least_load: { icon: Route, label: "Least Load", color: C.emerald, bg: C.emeraldSoft, border: "rgba(16,185,129,0.2)" },
};

export default function Routing() {
  const [formOpen, setFormOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const { t } = useLanguage();

  React.useEffect(() => {
    const id = "routing-dark-css";
    if (document.getElementById(id)) return;
    const el = document.createElement("style");
    el.id = id;
    el.textContent = DARK_CSS;
    document.head.appendChild(el);
    return () => document.getElementById(id)?.remove();
  }, []);

  const queryClient = useQueryClient();

  const { data: rules = [], isLoading } = useQuery({
    queryKey: ["routing-rules"],
    queryFn: () => RoutingRule.list("priority"),
  });

  const { data: providers = [] } = useQuery({
    queryKey: ["providers"],
    queryFn: () => Provider.list("priority"),
  });

  const { data: merchants = [] } = useQuery({
    queryKey: ["merchants"],
    queryFn: () => Merchant.list(),
  });

  const createMutation = useMutation({
    mutationFn: (data) => RoutingRule.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["routing-rules"]);
      setFormOpen(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => RoutingRule.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["routing-rules"]);
      setFormOpen(false);
      setSelectedRule(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => RoutingRule.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["routing-rules"]);
      setDeleteDialog(null);
    },
  });

  const handleSave = async (data) => {
    if (selectedRule) {
      await updateMutation.mutateAsync({ id: selectedRule.id, data });
    } else {
      await createMutation.mutateAsync(data);
    }
  };

  const handleEdit = (rule) => {
    setSelectedRule(rule);
    setFormOpen(true);
  };

  const toggleRuleStatus = async (rule) => {
    const newStatus = rule.status === "active" ? "inactive" : "active";
    await updateMutation.mutateAsync({ id: rule.id, data: { ...rule, status: newStatus } });
  };

  return (
    <div className="routing-dark" style={{ minHeight: "100vh", background: C.bg, fontFamily: "'Outfit',system-ui,sans-serif" }}>
      {/* Background Effects */}
      <div aria-hidden style={{ position: "fixed", inset: 0, pointerEvents: "none", backgroundImage: "linear-gradient(rgba(34,211,238,0.02) 1px,transparent 1px),linear-gradient(90deg,rgba(34,211,238,0.02) 1px,transparent 1px)", backgroundSize: "60px 60px", maskImage: "radial-gradient(ellipse 80% 80% at 50% 30%,black 20%,transparent 100%)" }} />
      <div aria-hidden style={{ position: "fixed", inset: 0, pointerEvents: "none", background: "radial-gradient(ellipse 1000px 600px at 5% 5%,rgba(34,211,238,0.04) 0%,transparent 70%),radial-gradient(ellipse 800px 500px at 95% 85%,rgba(59,130,246,0.04) 0%,transparent 65%)" }} />

      <div className="max-w-[1440px] mx-auto md:p-8 flex flex-col gap-6 relative z-10">
        {/* Header */}
        <div className="flex flex-col justify-between gap-4 sm:flex-row">
          <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
            <div style={{ width: 60, height: 60, borderRadius: 18, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, background: "linear-gradient(135deg,rgba(34,211,238,0.1) 0%,rgba(59,130,246,0.1) 100%)", border: "1px solid rgba(34,211,238,0.22)", boxShadow: "0 8px 24px rgba(34,211,238,0.1)" }}>
              <Route size={26} style={{ color: C.cyan }} />
            </div>
            <div>
              <h1 style={{ fontSize: 30, fontWeight: 800, color: C.t1, margin: 0, letterSpacing: "-0.6px" }}>{t("routingRulesTitle")}</h1>
              <p style={{ fontSize: 13, color: C.t3, margin: "6px 0 0", fontWeight: 500 }}>{t("configureProviderSelection")}</p>
            </div>
          </div>
          <button
            onClick={() => setFormOpen(true)}
            style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "0 20px", height: 44, borderRadius: 11, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "'Outfit',sans-serif", border: "1px solid rgba(34,211,238,0.3)", background: "linear-gradient(135deg,rgba(34,211,238,0.12) 0%,rgba(59,130,246,0.12) 100%)", color: C.cyan, transition: "all .2s cubic-bezier(0.4,0,0.2,1)", boxShadow: "0 4px 16px rgba(34,211,238,0.15)" }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(34,211,238,0.25)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 16px rgba(34,211,238,0.15)"; }}
          >
            <Plus size={16} />
            {t("addRule")}
          </button>
        </div>

        {/* Info Banner */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 14, padding: 18, borderRadius: 16, background: C.blueSoft, border: `1px solid rgba(59,130,246,0.22)`, marginBottom: 24 }}>
          <Route size={20} style={{ color: C.blue, flexShrink: 0, marginTop: 2 }} />
          <div>
            <h4 style={{ fontSize: 14, fontWeight: 700, color: C.blue, margin: 0 }}>{t("howRoutingWorks")}</h4>
            <p style={{ fontSize: 13, color: C.t2, margin: "4px 0 0", lineHeight: 1.6 }}>{t("routingDescription")}</p>
          </div>
        </div>

        {/* Rules List */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {rules.length === 0 ? (
            <div style={{ background: `linear-gradient(155deg,${C.cardHigh} 0%,${C.card} 100%)`, border: `1px solid ${C.border}`, borderRadius: 20, padding: 60, textAlign: "center", boxShadow: "0 4px 24px rgba(0,0,0,0.2)" }}>
              <div style={{ width: 64, height: 64, borderRadius: 16, background: C.cyanSoft, border: `1px solid rgba(34,211,238,0.2)`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
                <Route size={28} style={{ color: C.cyan }} />
              </div>
              <h3 style={{ fontSize: 18, fontWeight: 700, color: C.t1, margin: "0 0 8px" }}>{t("noRoutingRules")}</h3>
              <p style={{ fontSize: 13, color: C.t3, margin: "0 0 24px", fontWeight: 500 }}>{t("createFirstRule")}</p>
              <button
                onClick={() => setFormOpen(true)}
                style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "0 20px", height: 44, borderRadius: 11, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "'Outfit',sans-serif", border: "1px solid rgba(34,211,238,0.3)", background: "linear-gradient(135deg,rgba(34,211,238,0.12) 0%,rgba(59,130,246,0.12) 100%)", color: C.cyan, transition: "all .2s cubic-bezier(0.4,0,0.2,1)", boxShadow: "0 4px 16px rgba(34,211,238,0.15)" }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(34,211,238,0.25)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 16px rgba(34,211,238,0.15)"; }}
              >
                <Plus size={16} />
                {t("addRule")}
              </button>
            </div>
          ) : (
            rules.map((rule, index) => {
              const mode = routingModeConfig[rule.routing_mode] || routingModeConfig.priority;
              const ModeIcon = mode.icon;
              const providerCodes = rule.provider_sequence ? JSON.parse(rule.provider_sequence) : [];
              const isActive = rule.status === "active";

              return (
                <div
                  key={rule.id}
                  style={{ background: `linear-gradient(155deg,${C.cardHigh} 0%,${C.card} 100%)`, border: `1px solid ${C.border}`, borderRadius: 18, padding: 24, boxShadow: "0 4px 24px rgba(0,0,0,0.2)", transition: "all .3s cubic-bezier(0.4,0,0.2,1)", position: "relative", overflow: "hidden", opacity: isActive ? 1 : 0.6 }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 12px 36px rgba(34,211,238,0.12)"; e.currentTarget.style.borderColor = C.borderHover; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 24px rgba(0,0,0,0.2)"; e.currentTarget.style.borderColor = C.border; }}
                >
                  <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 1, background: "linear-gradient(90deg,transparent,rgba(34,211,238,0.15),transparent)" }} />

                  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 16, flex: 1 }}>
                      <div style={{ width: 40, height: 40, borderRadius: 10, background: "rgba(255,255,255,0.03)", border: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "grab", transition: "all .2s" }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(34,211,238,0.08)"; e.currentTarget.style.borderColor = "rgba(34,211,238,0.3)"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.03)"; e.currentTarget.style.borderColor = C.border; }}
                      >
                        <GripVertical size={18} style={{ color: C.t3 }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
                          <h3 style={{ fontSize: 16, fontWeight: 700, color: C.t1, margin: 0 }}>{rule.name === "Default Rule" ? t("defaultRule") : rule.name}</h3>
                          <span style={{ display: "inline-flex", padding: "3px 10px", borderRadius: 7, fontSize: 11, fontWeight: 700, background: "rgba(255,255,255,0.05)", border: `1px solid ${C.border}`, color: C.t3 }}>
                            {t("priority")} {rule.priority}
                          </span>
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 12px", borderRadius: 8, fontSize: 11, fontWeight: 700, background: isActive ? C.emeraldSoft : C.slateSoft, border: `1px solid ${isActive ? "rgba(16,185,129,0.2)" : "rgba(100,116,139,0.2)"}`, color: isActive ? C.emerald : C.slate }}>
                            {isActive ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
                            {rule.status}
                          </span>
                        </div>

                        {/* Conditions */}
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
                          <span style={{ display: "inline-flex", padding: "4px 10px", borderRadius: 7, fontSize: 11, fontWeight: 600, background: "rgba(255,255,255,0.04)", border: `1px solid ${C.border}`, color: C.t2 }}>
                            {t("type")}: {rule.transaction_type || t("allTypes").toLowerCase()}
                          </span>
                          <span style={{ display: "inline-flex", padding: "4px 10px", borderRadius: 7, fontSize: 11, fontWeight: 600, background: "rgba(255,255,255,0.04)", border: `1px solid ${C.border}`, color: C.t2 }}>
                            {t("method")}: {rule.payment_method?.replace(/_/g, " ") || t("allMethods").toLowerCase()}
                          </span>
                          <span style={{ display: "inline-flex", padding: "4px 10px", borderRadius: 7, fontSize: 11, fontWeight: 600, background: "rgba(255,255,255,0.04)", border: `1px solid ${C.border}`, color: C.t2 }}>
                            {t("amount")}: ฿{rule.min_amount?.toLocaleString() || 0} - ฿{rule.max_amount?.toLocaleString() || "∞"}
                          </span>
                          {rule.merchant_id && rule.merchant_id !== "all" && (
                            <span style={{ display: "inline-flex", padding: "4px 10px", borderRadius: 7, fontSize: 11, fontWeight: 600, background: "rgba(255,255,255,0.04)", border: `1px solid ${C.border}`, color: C.t2 }}>
                              {t("merchant")}: {merchants.find(m => m.id === rule.merchant_id)?.code || rule.merchant_id}
                            </span>
                          )}
                        </div>

                        {/* Provider Sequence */}
                        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 12px", borderRadius: 8, fontSize: 11, fontWeight: 700, background: mode.bg, border: `1px solid ${mode.border}`, color: mode.color }}>
                            <ModeIcon size={13} />
                            {mode.label}
                          </span>
                          <ArrowRight size={15} style={{ color: C.t4 }} />
                          {providerCodes.map((code, i) => (
                            <React.Fragment key={code}>
                              {i > 0 && <ArrowRight size={15} style={{ color: C.t4 }} />}
                              <span style={{ display: "inline-flex", padding: "4px 10px", borderRadius: 7, fontSize: 11, fontWeight: 700, background: "rgba(255,255,255,0.04)", border: `1px solid ${C.border}`, color: C.cyan, fontFamily: "monospace" }}>
                                {code}
                              </span>
                            </React.Fragment>
                          ))}
                          {rule.failover_enabled && (
                            <span style={{ display: "inline-flex", padding: "4px 10px", borderRadius: 7, fontSize: 10, fontWeight: 700, background: C.amberSoft, border: `1px solid rgba(245,158,11,0.2)`, color: C.amber }}>
                              Failover ({rule.max_failover_attempts}x)
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button style={{ width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 10, cursor: "pointer", background: C.card, border: `1px solid ${C.border}`, color: C.t2, transition: "all .2s" }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = C.cardHover; e.currentTarget.style.borderColor = C.borderHover; e.currentTarget.style.color = C.cyan; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = C.card; e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.t2; }}
                        >
                          <MoreHorizontal size={16} />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleEdit(rule)}>
                          <Pencil className="h-4 w-4 mr-2" />
                          {t("edit")}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => toggleRuleStatus(rule)}>
                          {rule.status === "active" ? (
                            <>
                              <XCircle className="h-4 w-4 mr-2" />
                              {t("disable")}
                            </>
                          ) : (
                            <>
                              <CheckCircle2 className="h-4 w-4 mr-2" />
                              {t("enable")}
                            </>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setDeleteDialog(rule)} className="text-red-600">
                          <Trash2 className="h-4 w-4 mr-2" />
                          {t("delete")}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Rule Form Dialog */}
        <RuleFormDialog
          rule={selectedRule}
          open={formOpen}
          onClose={() => {
            setFormOpen(false);
            setSelectedRule(null);
          }}
          onSave={handleSave}
          providers={providers}
          merchants={merchants}
        />

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t("deleteRoutingRule")}</AlertDialogTitle>
              <AlertDialogDescription>
                {t("deleteConfirmation")} "{deleteDialog?.name === "Default Rule" ? t("defaultRule") : deleteDialog?.name}"? {t("affectTransactionRouting")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => deleteMutation.mutate(deleteDialog.id)}
                className="bg-red-600 hover:bg-red-700"
              >
                {t("delete")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

function RuleFormDialog({ rule, open, onClose, onSave, providers, merchants }) {
  const { t } = useLanguage();
  const [formData, setFormData] = useState(
    rule || {
      name: "",
      status: "active",
      priority: 1,
      merchant_id: "all",
      transaction_type: "all",
      payment_method: "all",
      min_amount: 0,
      max_amount: 999999999,
      provider_sequence: "[]",
      routing_mode: "priority",
      failover_enabled: true,
      max_failover_attempts: 3,
      notes: "",
    }
  );
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (rule) {
      setFormData(rule);
    } else {
      setFormData({
        name: "",
        status: "active",
        priority: 1,
        merchant_id: "all",
        transaction_type: "all",
        payment_method: "all",
        min_amount: 0,
        max_amount: 999999999,
        provider_sequence: "[]",
        routing_mode: "priority",
        failover_enabled: true,
        max_failover_attempts: 3,
        notes: "",
      });
    }
  }, [rule, open]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const providerSequence = React.useMemo(() => {
    try {
      return JSON.parse(formData.provider_sequence || "[]");
    } catch {
      return [];
    }
  }, [formData.provider_sequence]);

  const toggleProvider = (code) => {
    const newSequence = providerSequence.includes(code)
      ? providerSequence.filter((c) => c !== code)
      : [...providerSequence, code];
    handleChange("provider_sequence", JSON.stringify(newSequence));
  };

  const handleSubmit = async () => {
    setSaving(true);
    await onSave(formData);
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto routing-dark">
        <DialogHeader>
          <DialogTitle>{rule ? t("editRoutingRule") : t("createRoutingRule")}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t("ruleName")} *</Label>
              <Input
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                placeholder={t("defaultQrRoutePlaceholder")}
              />
            </div>
            <div className="space-y-2">
              <Label>{t("priority")}</Label>
              <Input
                type="number"
                value={formData.priority}
                onChange={(e) => handleChange("priority", parseInt(e.target.value))}
                min={1}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t("transactionType")}</Label>
              <Select
                value={formData.transaction_type}
                onValueChange={(v) => handleChange("transaction_type", v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("allTypes")}</SelectItem>
                  <SelectItem value="deposit">{t("depositsOnly")}</SelectItem>
                  <SelectItem value="withdrawal">{t("withdrawalsOnly")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t("paymentMethod")}</Label>
              <Select
                value={formData.payment_method}
                onValueChange={(v) => handleChange("payment_method", v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("allMethods")}</SelectItem>
                  <SelectItem value="promptpay_qr">PromptPay QR</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="truemoney">TrueMoney</SelectItem>
                  <SelectItem value="linepay">LinePay</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t("minAmount")}</Label>
              <Input
                type="number"
                value={formData.min_amount}
                onChange={(e) => handleChange("min_amount", parseInt(e.target.value))}
              />
            </div>
            <div className="space-y-2">
              <Label>{t("maxAmount")}</Label>
              <Input
                type="number"
                value={formData.max_amount}
                onChange={(e) => handleChange("max_amount", parseInt(e.target.value))}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>{t("specificMerchant")}</Label>
            <Select
              value={formData.merchant_id || "all"}
              onValueChange={(v) => handleChange("merchant_id", v)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("allMerchants")}</SelectItem>
                {merchants.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.name} ({m.code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>{t("routingMode")}</Label>
            <Select
              value={formData.routing_mode}
              onValueChange={(v) => handleChange("routing_mode", v)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="priority">{t("priorityMode")}</SelectItem>
                <SelectItem value="round_robin">{t("roundRobin")}</SelectItem>
                <SelectItem value="weighted">{t("weighted")}</SelectItem>
                <SelectItem value="least_load">{t("leastLoad")}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>{t("providerSequence")}</Label>
            <div className="flex flex-wrap gap-2 p-4 rounded-lg min-h-[60px]" style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${C.border}` }}>
              {providers.map((p) => {
                const isSelected = providerSequence.includes(p.code);
                return (
                  <span
                    key={p.id}
                    onClick={() => toggleProvider(p.code)}
                    style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer", transition: "all .2s", background: isSelected ? C.cyanSoft : "rgba(255,255,255,0.04)", border: `1px solid ${isSelected ? "rgba(34,211,238,0.3)" : C.border}`, color: isSelected ? C.cyan : C.t2 }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(34,211,238,0.15)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
                  >
                    {p.code}
                    {isSelected && (
                      <span style={{ fontSize: 10, opacity: 0.7 }}>
                        #{providerSequence.indexOf(p.code) + 1}
                      </span>
                    )}
                  </span>
                );
              })}
            </div>
            <p style={{ fontSize: 11, color: C.t3, margin: "4px 0 0" }}>
              {t("clickProvidersOrder")}
            </p>
          </div>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: 16, borderRadius: 12, background: "rgba(255,255,255,0.03)", border: `1px solid ${C.border}` }}>
            <div>
              <Label style={{ fontSize: 13, fontWeight: 600, color: C.t1 }}>{t("enableFailover")}</Label>
              <p style={{ fontSize: 12, color: C.t3, margin: "2px 0 0" }}>{t("autoTryNextProvider")}</p>
            </div>
            <Switch
              checked={formData.failover_enabled}
              onCheckedChange={(v) => handleChange("failover_enabled", v)}
            />
          </div>

          {formData.failover_enabled && (
            <div className="space-y-2">
              <Label>{t("maxFailoverAttempts")}</Label>
              <Input
                type="number"
                value={formData.max_failover_attempts}
                onChange={(e) => handleChange("max_failover_attempts", parseInt(e.target.value))}
                min={1}
                max={10}
              />
            </div>
          )}
        </div>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={onClose}>
            {t("cancel")}
          </Button>
          <Button onClick={handleSubmit} disabled={saving || !formData.name}>
            {saving ? t("saving") : rule ? t("update") : t("create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}